import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header";

import {
  ProductService,
  ProductServiceService,
  ProductServiceRequest,
  ProductServiceType,
} from "./productService.service";
import ProductServiceForm from "./ProductServiceForm";
import { useProductServices } from "./useProductServices";
import {
  showConfirmationAlert,
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import { useTenantChange } from "../../../hooks/useTenantChange";
import { AuthContext } from "../../../context/authcontext";
import TableFilters, {
  FilterState,
  FilterConfig,
} from "../../../components/filters/TableFilters";
import SortableHeader from "../../../components/filters/SortableHeader";
import TablePagination from "../../../components/pagination/TablePagination";
import { getAuthHeaders } from "../../../utils/authUtils";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import ProductServiceFilterDropdown from "./ProductServiceFilterDropdown";

const ProductServices = () => {
  const navigate = useNavigate();
  const { selectedTenantId, auth } = useContext(AuthContext);

  const selectedTenant = auth?.tenants?.find(
    (t: any) => String(t.tenant_id) === String(selectedTenantId)
  );
  const userRole = selectedTenant?.role_key || selectedTenant?.role_name || "";
  const isSalesUser = userRole === "SALESUSER";
  const {
    productServices,
    loading,
    backgroundLoading,
    error,
    pagination,
    addProductService,
    updateProductService,
    removeProductService,
    fetchProductServices,
    isInitialized,
    clearBackgroundLoading,
    clearProductServicesData,
  } = useProductServices();

  const [tenantLoading, setTenantLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ProductService | null>(null);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string | null>(
    null
  );

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null,
    sortBy: "created_datetime",
    sortOrder: "desc",
  });

  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [descriptionModalData, setDescriptionModalData] = useState<{ name: string; description: string } | null>(null);

  useTenantChange({
    onTenantChange: () => {
      clearProductServicesData();
      setFilteredData([]);
      setIsFilterActive(false);
      setCurrentPage(1);
      setSelectedItems([]);
      setSelectAll(false);
      setSelectedTypeFilter(null);
    },
    refreshData: () => fetchProductServices(1, 12, { is_active: true }),
    clearData: clearProductServicesData,
  });

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchField: "product_n_service_name",
      showDateFilter: false,
      dateField: "created_datetime",
      showIndustryFilter: false,
      showCountryFilter: false,
      showStatusFilter: false,
      defaultSort: {
        field: "created_datetime",
        order: "desc" as const,
      },
    }),
    []
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterState) => {
      setFilters(newFilters);
      setCurrentPage(1);
      fetchProductServices(1, 12, { ...newFilters, is_active: true });
    },
    [fetchProductServices]
  );

  const handleTypeFilterChange = useCallback(
    (typeFilter: string | null) => {
      setSelectedTypeFilter(typeFilter);
      setCurrentPage(1);
      fetchProductServices(1, 12, { ...filters, typeFilter, is_active: true });
    },
    [filters, fetchProductServices]
  );

  const handleApiFilterChange = useCallback(
    (apiFilters: Record<string, any>) => { },
    []
  );

  const handleFiltersReset = useCallback(() => {
    const resetFilters = {
      searchTerm: "",
      dateRange: [null, null] as [Date | null, Date | null],
      industryFilter: [],
      countryFilter: [],
      statusFilter: null,
      sortBy: "created_datetime",
      sortOrder: "desc" as const,
    };

    setFilters(resetFilters);
    setSelectedTypeFilter(null);
    setCurrentPage(1);
    fetchProductServices(1, pagination.pageSize || 12, {
      ...resetFilters,
      is_active: true,
    });
  }, [fetchProductServices, pagination.pageSize]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchProductServices(page, 12, { ...filters, is_active: true });
  };

  const handleApiCall = useCallback(
    async (url: string) => {
      if (!selectedTenantId) {
        return;
      }

      try {
        setIsFilterLoading(true);
        const headers = getAuthHeaders();
        const response = await fetch(url, {
          method: "GET",
          headers: headers,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.results && Array.isArray(result.results)) {
          setFilteredData(result.results);
          setIsFilterActive(true);
          setCurrentPage(1);
        } else if (result.data && Array.isArray(result.data)) {
          setFilteredData(result.data);
          setIsFilterActive(true);
          setCurrentPage(1);
        } else if (Array.isArray(result)) {
          setFilteredData(result);
          setIsFilterActive(true);
          setCurrentPage(1);
        } else {
          setFilteredData([]);
          setIsFilterActive(true);
          setCurrentPage(1);
        }
      } catch (error: any) {
        console.error("Filter API error:", error);

        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("authCred");
          localStorage.removeItem("authResponse");
          localStorage.removeItem("authTimestamp");
          localStorage.removeItem("selectedTenantId");
          navigate("/login");
          return;
        }

        setFilteredData([]);
        setIsFilterActive(false);
      } finally {
        setIsFilterLoading(false);
      }
    },
    [selectedTenantId, navigate]
  );

  const handleSortChange = useCallback(
    (newSortBy: string) => {
      const newOrder: "asc" | "desc" =
        filters.sortBy === newSortBy && filters.sortOrder === "asc"
          ? "desc"
          : "asc";

      const newFilters = {
        ...filters,
        sortBy: newSortBy,
        sortOrder: newOrder,
      };
      setFilters(newFilters);
    },
    [filters]
  );

  const { filteredItems, totalFilteredCount } = useMemo(() => {
    if (isFilterActive) {
      return {
        filteredItems: filteredData,
        totalFilteredCount: filteredData.length,
      };
    }

    if (!Array.isArray(productServices)) {
      return { filteredItems: [], totalFilteredCount: 0 };
    }

    return {
      filteredItems: productServices,
      totalFilteredCount: pagination.totalCount,
    };
  }, [productServices, filteredData, isFilterActive, pagination.totalCount]);

  const paginatedItems = useMemo(() => {
    if (isFilterActive) {
      const startIndex = (currentPage - 1) * 12;
      const endIndex = startIndex + 12;
      return filteredItems.slice(startIndex, endIndex);
    }
    return filteredItems;
  }, [filteredItems, isFilterActive, currentPage]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const validIds = paginatedItems
        .filter((item) => item.product_n_service_id)
        .map((item) => item.product_n_service_id as number);
      setSelectedItems(validIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id: number) => {
    setSelectedItems((prev) => {
      const newSelected = prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id];

      const validCurrentData = paginatedItems.filter(
        (item) => item.product_n_service_id
      );
      setSelectAll(
        newSelected.length === validCurrentData.length &&
        validCurrentData.length > 0
      );
      return newSelected;
    });
  };

  useEffect(() => {
    if (paginatedItems.length > 0) {
      const validCurrentData = paginatedItems.filter(
        (item) => item.product_n_service_id
      );
      const allCurrentSelected = validCurrentData.every(
        (item) =>
          item.product_n_service_id &&
          selectedItems.includes(item.product_n_service_id)
      );
      setSelectAll(allCurrentSelected && selectedItems.length > 0);
    } else {
      setSelectAll(false);
    }
  }, [paginatedItems, selectedItems]);

  useEffect(() => {
    setSelectedItems([]);
    setSelectAll(false);
  }, [currentPage]);

  useEffect(() => {
    if (!isInitialized) {
      fetchProductServices(1, 12, { is_active: true });
    }
  }, [isInitialized, fetchProductServices]);

  useEffect(() => {
    if (isInitialized && backgroundLoading) {
      const fetchBackgroundData = async () => {
        try {
        } catch (error) {
          console.error("Background API error:", error);
        } finally {
          clearBackgroundLoading();
        }
      };

      fetchBackgroundData();
    }
  }, [isInitialized, backgroundLoading, clearBackgroundLoading]);

  useEffect(() => {
    if (pagination.currentPage && pagination.currentPage !== currentPage) {
      setCurrentPage(pagination.currentPage);
    }
  }, [pagination.currentPage, currentPage]);

  useEffect(() => {
    const editOffcanvas = document.getElementById(
      "offcanvas_edit_product_service"
    );
    const addOffcanvas = document.getElementById(
      "offcanvas_add_product_service"
    );

    const handleEditHidden = () => {
      setSelectedItem(null);
    };

    const handleAddHidden = () => { };

    if (editOffcanvas) {
      editOffcanvas.addEventListener("hidden.bs.offcanvas", handleEditHidden);
    }

    if (addOffcanvas) {
      addOffcanvas.addEventListener("hidden.bs.offcanvas", handleAddHidden);
    }

    return () => {
      if (editOffcanvas) {
        editOffcanvas.removeEventListener(
          "hidden.bs.offcanvas",
          handleEditHidden
        );
      }
      if (addOffcanvas) {
        addOffcanvas.removeEventListener(
          "hidden.bs.offcanvas",
          handleAddHidden
        );
      }
    };
  }, []);

  const handleCreate = async (formData: Partial<ProductServiceRequest>) => {
    setIsCreating(true);
    try {
      const payloadWithUserInfo = {
        ...formData,
        created_by: auth?.user_id,
        updated_by: auth?.user_id,
      };
      const newItem = await ProductServiceService.createProductService(
        payloadWithUserInfo,
        selectedTenantId!
      );
      addProductService(newItem);

      const offcanvasElement = document.getElementById(
        "offcanvas_add_product_service"
      );
      if (offcanvasElement) {
        const closeBtn = offcanvasElement.querySelector(
          '[data-bs-dismiss="offcanvas"]'
        );
        if (closeBtn) {
          (closeBtn as HTMLElement).click();
        }
      }

      setTimeout(() => {
        showSuccessAlert(
          `Product/Service "${newItem.product_n_service_name}" created successfully!`
        );
      }, 500);
    } catch (err) {
      console.error("Failed to create product/service:", err);
      showErrorAlert("Failed to create product/service. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdate = async (
    id: number,
    formData: Partial<ProductServiceRequest>
  ) => {
    setIsUpdating(true);
    try {
      const payloadWithUserInfo = {
        ...formData,
        updated_by: auth?.user_id,
      };
      const updatedItem = await ProductServiceService.updateProductService(
        id,
        payloadWithUserInfo,
        selectedTenantId!
      );
      updateProductService(updatedItem);

      const offcanvasElement = document.getElementById(
        "offcanvas_edit_product_service"
      );
      if (offcanvasElement) {
        const closeBtn = offcanvasElement.querySelector(
          '[data-bs-dismiss="offcanvas"]'
        );
        if (closeBtn) {
          (closeBtn as HTMLElement).click();
        }
      }

      setTimeout(() => {
        showSuccessAlert(
          `Product/Service "${updatedItem.product_n_service_name}" updated successfully!`
        );
      }, 500);
    } catch (err) {
      console.error("Failed to update product/service:", err);
      showErrorAlert("Failed to update product/service. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (item: ProductService) => {
    const result = await showConfirmationAlert(
      "Delete Product/Service",
      `Are you sure you want to delete "${item.product_n_service_name}"? This action cannot be undone.`
    );

    if (result.isConfirmed) {
      try {
        if (item.product_n_service_id) {
          await ProductServiceService.deleteProductService(
            item.product_n_service_id
          );
          removeProductService(item.product_n_service_id);

          await showSuccessAlert(
            `Product/Service "${item.product_n_service_name}" deleted successfully!`
          );
        }
      } catch (err) {
        console.error("Failed to delete product/service:", err);
        await showErrorAlert(
          "Failed to delete product/service. Please try again."
        );
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    setIsBulkDeleting(true);
    try {
      const deletePromises = selectedItems.map((id) =>
        ProductServiceService.deleteProductService(id)
      );

      await Promise.all(deletePromises);

      selectedItems.forEach((id) => removeProductService(id));

      setSelectedItems([]);
      setSelectAll(false);
      setShowBulkDeleteModal(false);

      await showSuccessAlert(
        `${selectedItems.length} product/service${selectedItems.length > 1 ? "s" : ""
        } deleted successfully!`
      );
    } catch (err) {
      console.error("Failed to delete products/services:", err);
      await showErrorAlert(
        "Failed to delete some products/services. Please try again."
      );
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (!modal) {
      console.warn(`Offcanvas element not found: ${offcanvasId}`);
      return;
    }

    if (typeof window !== "undefined" && (window as any).bootstrap) {
      try {
        let bsOffcanvas = (window as any).bootstrap.Offcanvas.getInstance(
          modal
        );

        if (bsOffcanvas) {
          bsOffcanvas.hide();
        } else {
          const closeButton = modal.querySelector(
            '[data-bs-dismiss="offcanvas"]'
          );
          if (closeButton) {
            (closeButton as HTMLElement).click();
          } else {
            bsOffcanvas = (
              window as any
            ).bootstrap.Offcanvas.getOrCreateInstance(modal);
            if (bsOffcanvas) {
              bsOffcanvas.hide();
            }
          }
        }
      } catch (error) {
        console.error("Error closing offcanvas:", error);

        try {
          const closeButton = modal.querySelector(
            '.btn-close, [data-bs-dismiss="offcanvas"]'
          );
          if (closeButton) {
            (closeButton as HTMLElement).click();
          }
        } catch (fallbackError) {
          console.error("Fallback close also failed:", fallbackError);
        }
      }
    }
  };

  const columns = [
    ...(isSalesUser
      ? []
      : [
        {
          title: (
            <div className="form-check form-check-md">
              <input
                className="form-check-input"
                type="checkbox"
                id="select-all"
                checked={!!selectAll}
                onChange={handleSelectAll}
              />
            </div>
          ),
          dataIndex: "checkbox",
          render: (text: string, record: ProductService) => (
            <div className="form-check form-check-md">
              <input
                className="form-check-input"
                type="checkbox"
                id={`select-${record.product_n_service_id}`}
                checked={
                  record.product_n_service_id
                    ? selectedItems.includes(record.product_n_service_id)
                    : false
                }
                onChange={() =>
                  record.product_n_service_id &&
                  handleSelectItem(record.product_n_service_id)
                }
              />
            </div>
          ),
        },
      ]),
    {
      title: (
        <SortableHeader
          title="Product / Service Name"
          field="product_n_service_name"
          currentSort={filters.sortBy}
          currentOrder={filters.sortOrder}
          onSort={handleSortChange}
        />
      ),
      dataIndex: "product_n_service_name",
      width: "30%",
      render: (name: string, record: ProductService) => (
        <div>{record.product_n_service_name}</div>
      ),
    },
    {
      title: "Description",
      dataIndex: "product_n_service_description",
      width: "30%",
      render: (description: string, record: ProductService) => {
        if (!description) return <div>-</div>;
        if (description.length <= 35) return <div>{description}</div>;
        return (
          <div>
            {description.substring(0, 35)}...{" "}
            <span
              style={{ color: "#0d6efd", cursor: "pointer", fontSize: "0.85em" }}
              onClick={() => {
                setDescriptionModalData({
                  name: record.product_n_service_name,
                  description,
                });
                setShowDescriptionModal(true);
              }}
            >
              Read More
            </span>
          </div>
        );
      },
    },

    {
      title: (
        <div className="table-header-with-divider">
          Type
          <div className="column-divider"></div>
        </div>
      ),
      dataIndex: "product_n_service_type",
      render: (type: ProductServiceType, record: ProductService) => (
        <div>
          {record.product_n_service_type?.product_n_service_type_display_name ||
            "Unknown"}
        </div>
      ),
    },
    {
      title: (
        <SortableHeader
          title="Price"
          field="product_n_service_unit_price"
          currentSort={filters.sortBy}
          currentOrder={filters.sortOrder}
          onSort={handleSortChange}
        />
      ),
      dataIndex: "product_n_service_unit_price",
      render: (price: number, record: ProductService) => (
        <div>
          {record.currency_code || "$"} {price || "0.00"}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (isActive: boolean) => (
        <div>
          <span
            className={`badge ${isActive ? "badge-soft-success" : "badge-soft-danger"
              }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
      ),
    },
    ...(isSalesUser
      ? []
      : [
        {
          title: "Actions",
          dataIndex: "actions",
          render: (text: string, record: ProductService) => (
            <div className="dropdown table-action">
              <Link
                to="#"
                className="action-icon"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                data-bs-boundary="viewport"
                data-bs-popper-config='{"strategy":"fixed"}'
              >
                <i className="fa fa-ellipsis-v"></i>
              </Link>
              <div className="dropdown-menu dropdown-menu-end">
                <Link
                  className="dropdown-item"
                  to="#"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvas_edit_product_service"
                  onClick={() => setSelectedItem(record)}
                >
                  <i className="ti ti-edit text-blue"></i> Edit
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={() => handleDelete(record)}
                >
                  <i className="ti ti-trash text-danger"></i> Delete
                </Link>
              </div>
            </div>
          ),
        },
      ]),
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Settings</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body pb-0 pt-2">
                <SettingsTabsNavigation
                  activeTab="products-services"
                  variant="bottom"
                  includeIcons={true}
                  context="organization"
                />
              </div>
            </div>

            <div className="card" style={{ position: "relative" }}>
              {(loading || tenantLoading) && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255)",
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "not-allowed",
                  }}
                >
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading</span>
                    </div>
                    <div className="mt-2">
                      <small className="text-muted">
                        Loading Products & Services
                      </small>
                    </div>
                  </div>
                </div>
              )}
              <div className="card-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <h4 className="fw-semibold mb-0">Products & Services</h4>
                  </div>
                  <div className="col-sm-8">
                    <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                      {!isSalesUser && selectedItems.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-danger ms-3"
                          onClick={() => setShowBulkDeleteModal(true)}
                          disabled={isBulkDeleting}
                        >
                          <i className="ti ti-trash me-2" />
                          Delete Selected ({selectedItems.length})
                        </button>
                      )}{" "}
                      &nbsp;
                      {!isSalesUser && (
                        <button
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add_product_service"
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add Product/Service
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                <div className="d-flex align-items-center flex-wrap row-gap-2">
                  <TableFilters
                    config={filterConfig}
                    data={productServices}
                    onFilterChange={handleFilterChange}
                    onApiFilterChange={handleApiFilterChange}
                    onApiCall={handleApiCall}
                    onFiltersReset={handleFiltersReset}
                    tenantId={selectedTenantId || undefined}
                    className="mt-4"
                  />
                </div>
                <div className="d-flex align-items-center flex-wrap row-gap-2">
                  <ProductServiceFilterDropdown
                    onFilterChange={handleTypeFilterChange}
                    currentFilters={{
                      searchTerm: filters.searchTerm,
                      dateRange: filters.dateRange,
                      typeFilter: selectedTypeFilter,
                    }}
                    loading={loading}
                  />
                </div>
              </div>

              {loading || tenantLoading ? (
                <div className="card-body">
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2">
                      <small className="text-muted">
                        Loading Products & Services...
                      </small>
                    </div>
                  </div>
                </div>
              ) : error ? (
                <div className="card-body">
                  <div className="alert alert-danger text-center">
                    <i className="ti ti-alert-circle me-2"></i>
                    {error}
                  </div>
                </div>
              ) : (
                <div className="card-body">
                  <div
                    className="table-responsive contact-table-wrapper"
                    style={{ position: "relative" }}
                  >
                    {isFilterLoading && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: "rgba(255, 255, 255, 1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 10,
                          minHeight: "200px",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span>Filtering results...</span>
                        </div>
                      </div>
                    )}
                    {paginatedItems.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="avatar avatar-xl bg-light-gray rounded-circle mb-3 mx-auto">
                          <i className="fa-solid fa-cart-shopping fs-40 text-gray"></i>
                        </div>
                        <h4 className="text-gray mb-2">
                          No Products & Services Found!
                        </h4>
                        <p className="text-muted mb-3">
                          There are no products & services to display at the
                          moment.
                        </p>
                        {!isSalesUser && (
                          <button
                            className="btn btn-primary"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#offcanvas_add_product_service"
                          >
                            <i className="fa fa-plus me-2" />
                            Add Your First Product/Service
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <Table
                          columns={columns}
                          dataSource={paginatedItems}
                          rowKey={(record: ProductService) =>
                            record.product_n_service_id || 0
                          }
                          className="table table-striped contact-table"
                          pagination={false}
                          rowSelection={false}
                        />
                        <TablePagination
                          currentPage={pagination.currentPage || currentPage}
                          pageSize={12}
                          totalItems={
                            isFilterActive
                              ? totalFilteredCount
                              : pagination.totalCount
                          }
                          filteredItems={totalFilteredCount}
                          isFiltered={
                            filters.searchTerm !== "" ||
                            filters.dateRange[0] !== null ||
                            filters.dateRange[1] !== null
                          }
                          onPageChange={handlePageChange}
                          onPageSizeChange={() => { }}
                          entityName="Products & Services"
                          loading={loading}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isSalesUser && (
        <>
          <div
            className="offcanvas offcanvas-end"
            tabIndex={-1}
            id="offcanvas_add_product_service"
            style={{ maxWidth: "550px" }}
            data-bs-backdrop="static"
          >
            <div className="offcanvas-header border-bottom">
              <h5 className="fw-semibold">Add New Product/Service</h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                aria-label="Close"
                data-bs-dismiss="offcanvas"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="offcanvas-body">
              <ProductServiceForm
                productService={null}
                onSubmit={handleCreate}
                onCancel={() => closeOffcanvas("offcanvas_add_product_service")}
                isSubmitting={isCreating}
              />
            </div>
          </div>

          <div
            className="offcanvas offcanvas-end"
            tabIndex={-1}
            id="offcanvas_edit_product_service"
            style={{ width: "500px" }}
            data-bs-backdrop="static"
          >
            <div className="offcanvas-header border-bottom">
              <h5 className="fw-semibold">Edit Product/Service</h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                aria-label="Close"
                data-bs-dismiss="offcanvas"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="offcanvas-body">
              {selectedItem && (
                <ProductServiceForm
                  key={`edit-${selectedItem.product_n_service_id}`}
                  productService={selectedItem}
                  onSubmit={(formData) => {
                    if (selectedItem.product_n_service_id) {
                      return handleUpdate(
                        selectedItem.product_n_service_id,
                        formData
                      );
                    }
                    return Promise.resolve();
                  }}
                  onCancel={() =>
                    closeOffcanvas("offcanvas_edit_product_service")
                  }
                  isSubmitting={isUpdating}
                />
              )}
            </div>
          </div>
        </>
      )}

      {showDescriptionModal && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex={-1}
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowDescriptionModal(false);
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Description of {descriptionModalData?.name}</h5>
                  <i className="ti ti-x" style={{ cursor: "pointer" }} onClick={() => setShowDescriptionModal(false)} />
                </div>
                <div className="modal-body">
                  <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {descriptionModalData?.description}
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setShowDescriptionModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {!isSalesUser && (
        <Modal
          show={showBulkDeleteModal}
          onHide={() => setShowBulkDeleteModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Delete Selected Products & Services</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                <i className="ti ti-trash-x fs-36 text-danger" />
              </div>
              <h4 className="mb-2">Delete Selected Products & Services?</h4>
              <p className="mb-0">
                Are you sure you want to delete {selectedItems.length} selected{" "}
                {selectedItems.length > 1
                  ? "products/services"
                  : "product/service"}
                ?
                <br />
                This action cannot be undone.
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={() => setShowBulkDeleteModal(false)}
              disabled={isBulkDeleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
            >
              {isBulkDeleting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Deleting...
                </>
              ) : (
                `Yes, Delete ${selectedItems.length > 1 ? "All" : "It"}`
              )}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default ProductServices;
